import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { NumericInput } from "@/components/ui/numeric-input";
import { MoneyInput } from "@/components/ui/money-input";
import { formatErrorMessage } from "@/lib/errorMessages";
import { createTransfer, updateTransfer } from "@/lib/transfersApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const formSchema = z.object({
  transfer_code: z.string().min(1, "Transfer code is required"),
  emirate: z.string().min(1, "Emirate is required"),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  type: z.enum(["driver", "guide", "staff", "luggage_car"]),
  pricing_type: z.enum(["per_person", "per_family", "per_group"]),
  capacity: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseInt(val);
    return !isNaN(num) && num >= 0;
  }, "Capacity must be greater than or equal to 0"),
  price_op: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price OP must be greater than or equal to 0"),
  price_ag: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price AG must be greater than or equal to 0"),
  price_cf: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price CF must be greater than or equal to 0"),
  supplier_name: z.string().optional(),
  cost_supplier: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Cost Supplier must be greater than or equal to 0"),
  tax_percentage: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Tax percentage must be greater than or equal to 0"),
  image_url: z.string().optional(),
  hcte_notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface TransferFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  onSuccess: () => void;
}

const TransferForm = ({ open, onOpenChange, initialData, onSuccess }: TransferFormProps) => {
  const { toast } = useToast();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      transfer_code: "",
      emirate: "",
      title: "",
      description: "",
      type: "driver",
      pricing_type: "per_person",
      capacity: "",
      price_op: "",
      price_ag: "",
      price_cf: "",
      supplier_name: "",
      cost_supplier: "",
      tax_percentage: "",
      image_url: "",
      hcte_notes: "",
    },
  });

  const {
    handleOpenChange: handleUnsavedOpenChange,
    ConfirmDialog,
    pendingClose,
    setPendingClose,
  } = useUnsavedChanges({
    isDirty: form.formState.isDirty,
    onSave: () => form.handleSubmit(onSubmit)(),
    onDiscard: () => {
      form.reset();
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (pendingClose) {
      onOpenChange(false);
      setPendingClose(false);
    }
  }, [pendingClose, onOpenChange, setPendingClose]);

  useEffect(() => {
    if (initialData) {
      form.reset({
        transfer_code: initialData.transfer_code || "",
        emirate: initialData.emirate || "",
        title: initialData.title || "",
        description: initialData.description || "",
        type: initialData.type || "driver",
        pricing_type: initialData.pricing_type || "per_person",
        capacity: initialData.capacity?.toString() || "",
        price_op: initialData.price_op?.toString() || "",
        price_ag: initialData.price_ag?.toString() || "",
        price_cf: initialData.price_cf?.toString() || "",
        supplier_name: initialData.supplier_name || "",
        cost_supplier: initialData.cost_supplier?.toString() || "",
        tax_percentage: initialData.tax_percentage?.toString() || "",
        image_url: initialData.image_url || "",
        hcte_notes: initialData.hcte_notes || "",
      });
    } else {
      form.reset();
    }
  }, [initialData, form]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        transfer_code: data.transfer_code,
        emirate: data.emirate,
        title: data.title,
        description: data.description || null,
        type: data.type,
        pricing_type: data.pricing_type,
        capacity: data.capacity ? parseInt(data.capacity) : null,
        price_op: data.price_op ? parseFloat(data.price_op) : null,
        price_ag: data.price_ag ? parseFloat(data.price_ag) : null,
        price_cf: data.price_cf ? parseFloat(data.price_cf) : null,
        supplier_name: data.supplier_name || null,
        cost_supplier: data.cost_supplier ? parseFloat(data.cost_supplier) : null,
        tax_percentage: data.tax_percentage ? parseFloat(data.tax_percentage) : null,
        image_url: data.image_url || null,
        hcte_notes: data.hcte_notes || null,
      };

      if (initialData?.id) {
        await updateTransfer(initialData.id, payload);
        toast({ title: "Success", description: "Transfer updated successfully" });
      } else {
        await createTransfer(payload);
        toast({ title: "Success", description: "Transfer created successfully" });
      }

      form.reset();
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: formatErrorMessage(error, "Failed to save transfer"),
        variant: "destructive",
      });
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      const shouldClose = handleUnsavedOpenChange(open);
      if (shouldClose) {
        onOpenChange(false);
      }
    } else {
      onOpenChange(open);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? "Edit Transfer" : "Add New Transfer"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="transfer_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Transfer Code *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emirate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emirate *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="driver">Driver</SelectItem>
                        <SelectItem value="guide">Guide</SelectItem>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="luggage_car">Luggage Car</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pricing_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pricing Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="per_person">Per person</SelectItem>
                        <SelectItem value="per_family">Per family</SelectItem>
                        <SelectItem value="per_group">Per group</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="capacity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Capacity</FormLabel>
                  <FormControl>
                    <NumericInput
                      value={field.value}
                      onChange={(value) => field.onChange(value || "")}
                      onBlur={field.onBlur}
                      allowDecimals={false}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="price_op"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price OP (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_ag"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price AG (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_cf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price CF (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="supplier_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Supplier Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cost_supplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cost Supplier (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="tax_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax (%)</FormLabel>
                    <FormControl>
                      <NumericInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        allowDecimals={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="image_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image URL</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="hcte_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>HCTE Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={4} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    <ConfirmDialog />
    </>
  );
};

export default TransferForm;
